import { useEffect, useState } from 'react';
import { useNavigate, useParams } from "react-router";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { IoChevronBack } from "react-icons/io5";
import { schemaUtils } from "@centivo-business/utils";
import { useAccountFormStore } from "../../../store/accountFormStore";
import useAccountFormZustandPersist from "../../../hooks/useAccountFormZustandPersist";
import usePropagateAccountIdentifiers from "../../../hooks/usePropagateAccountIdentifiers";
import { ACTIONS, SUB_ACCOUNT_BUILDER_INSTRUCTIONS, SUB_ACCOUNT_DRAFT, TOP_ACCOUNT_BUILDER_INSTRUCTIONS } from "../../../utils/constants";
import Stepper from "../../shared/Stepper";
import TopAccountStepOne from './TopAccountStepOne';
import TopAccountStepTwo from './TopAccountStepTwo';
import TopAccountStepThree from './TopAccountStepThree';
import TopAccountStepFour from './TopAccountStepFour';
import TopAccountStepFive from './TopAccountStepFive';
import TopAccountStepSix from './TopAccountStepSix';
import SubAccountStepOne from './SubAccountStepOne';
import SubAccountStepTwo from './SubAccountStepTwo';
import SubAccountStepThree from './SubAccountStepThree';
import SubAccountStepFour from './SubAccountStepFour';

const ACCOUNT_DEFAULT_VALUES = {
    accountId: undefined,
    asOfDate: undefined,
    updateAllVersions: false,
    taxEntity: undefined,
    accountName: "",
    accountHccId: "",
    accountType: undefined,
    effectiveDate: undefined,
    renewalInterval: undefined,
    subscriptionsAllowed: false,
    ignorePrimaryAddressCheck: false,
    primaryAddress1: "",
    primaryAddress2: undefined,
    primaryAddress3: undefined,
    primaryAddressCity: "",
    primaryAddressState: undefined,
    primaryAddressZip: "",
    billingCycle: undefined,
    ignoreBillingAddressCheck: false,
    billingAddress1: "",
    billingAddress2: undefined,
    billingAddress3: undefined,
    billingAddressCity: "",
    billingAddressState: undefined,
    billingAddressZip: "",
    subAccountDraft: {
        parentAccount: undefined,
        accountName: "",
        accountHccId: "",
        accountType: undefined,
        subscriptionsAllowed: false,
        ignorePrimaryAddressCheck: false,
        primaryAddress1: "",
        primaryAddress2: undefined,
        primaryAddress3: undefined,
        primaryAddressCity: "",
        primaryAddressState: undefined,
        primaryAddressZip: "",
        billParent: false,
        billingCycle: undefined,
        ignoreBillingAddressCheck: false,
        billingAddress1: "",
        billingAddress2: undefined,
        billingAddress3: undefined,
        billingAddressCity: "",
        billingAddressState: undefined,
        billingAddressZip: "",
    },
    subAccounts: [],
    udts: {},
};

const AccountBuilderMultiStepForm = () => {
    const [instructions, setInstructions] = useState(TOP_ACCOUNT_BUILDER_INSTRUCTIONS);

    const navigate = useNavigate();
    const { accounts, removeAccountByAccountId, upsertAccount } = useAccountFormStore();

    const { accountId: encodedAccountId } = useParams();
    const accountId = decodeURIComponent(encodedAccountId);
    const activeAccount = accounts.find(account => account.accountId === accountId);
    const activeStep = activeAccount?.activeStep ?? 1;

    const methods = useForm({
        resolver: zodResolver(schemaUtils.accountSchema),
        mode: 'all',
        defaultValues: ACCOUNT_DEFAULT_VALUES,
        resetOptions: {
            keepDefaultValues: true,
        },
    });

    const { control, handleSubmit, reset, resetField, setValue, getValues, trigger } = methods;

    const action = useWatch({ control, name: 'action' });

    useAccountFormZustandPersist(accountId, {
        control,
        setValue,
        reset,
        exclude: [SUB_ACCOUNT_DRAFT],
    });

    usePropagateAccountIdentifiers(accountId, {
        control,
        setValue,
        getValues,
    });

    useEffect(() => {
        if (action === ACTIONS.CREATE) {
            setValue('asOfDate', new Date("1800-01-01"));
        }
    }, [action]);

    const onSubmit = () => {};

    const nextStepHandler = async (step) => {
        await trigger();
        setValue("activeStep", step + 1);
        upsertAccount({ activeStep: activeStep + 1, accountId });
    };

    const toggleInstructions = () => {
        if (instructions === SUB_ACCOUNT_BUILDER_INSTRUCTIONS) {
            resetField(SUB_ACCOUNT_DRAFT);
            setInstructions(TOP_ACCOUNT_BUILDER_INSTRUCTIONS);
            setValue("activeStep", 4);
            upsertAccount({ activeStep: 4, accountId });
        } else {
            setInstructions(SUB_ACCOUNT_BUILDER_INSTRUCTIONS);
            setValue("activeStep", 1);
            upsertAccount({ activeStep: 1, accountId });
        }
    };

    return (
        <div className="container" style={{ position: "relative" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                {instructions === SUB_ACCOUNT_BUILDER_INSTRUCTIONS ? (
                    <>
                        <div
                            className="backBtn"
                            style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                            onClick={toggleInstructions}
                        >
                            <IoChevronBack />
                            {"Back"}
                        </div>
                    </>
                ) : (
                    <>
                        <div className="title">{'Create Top Account'}</div>
                        <div
                            className="danger"
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                                removeAccountByAccountId(accountId);
                                navigate(`/config/account-builder`);
                            }}
                        >
                            Delete Draft
                        </div>
                    </>
                )}
            </div>

            <div className="steps-wrapper" style={{ padding: '1rem 0rem' }}>
                <Stepper
                    steps={instructions?.map(ins => ({ label: ins.instructions }))}
                    activeStep={activeStep}
                    onClickHandler={nextStepHandler}
                />
            </div>

            <br />
            <FormProvider {...methods}>
                <form
                    className="form scrollable-form"
                    onSubmit={handleSubmit(onSubmit)}
                    style={{
                        overflowY: "scroll",
                        scrollbarWidth: '1rem',
                        padding: "1rem",
                        flexGrow: 1,
                        width: "100%",
                        height: "100%",
                        position: "relative",
                    }}
                >
                    {instructions === SUB_ACCOUNT_BUILDER_INSTRUCTIONS ? (
                        <>
                            <SubAccountStepOne activeStep={activeStep} />
                            <SubAccountStepTwo activeStep={activeStep} />
                            <SubAccountStepThree activeStep={activeStep} />
                            <SubAccountStepFour activeStep={activeStep} onSaveSubAccountSucceeded={toggleInstructions} />
                        </>
                    ) : (
                        <>
                            <TopAccountStepOne activeStep={activeStep} />
                            <TopAccountStepTwo activeStep={activeStep} />
                            <TopAccountStepThree activeStep={activeStep} />
                            <TopAccountStepFour activeStep={activeStep} onCreateEditButtonClicked={toggleInstructions} />
                            <TopAccountStepFive activeStep={activeStep} />
                            <TopAccountStepSix activeStep={activeStep} />
                        </>
                    )}

                </form>
            </FormProvider>
        </div>
    );
};

export default AccountBuilderMultiStepForm;
